/**
 * Linie matek: nabídka běžně dostupných linií v ČR + vlastní linie včelaře
 * (ukládají se do nastavení a dají se v Nastavení přejmenovat nebo smazat).
 */
import { useState } from 'react';
import { getSetting, setSetting } from '../db/db';
import { useQuery } from './hooks';
import { Field, Section, TextInput, toast } from './ui';
import { confirmSmall } from './confirm';

/** Linie a plemena matek běžně nabízené chovateli v ČR. */
export const COMMON_QUEEN_LINES = ['Singer', 'Sklenar', 'Vigor', 'Troiseck', 'Cimala', 'Kývalka', 'Dol', 'Karpatka', 'Buckfast'];

const SETTING = 'customQueenLines';

const norm = (s: string) => s.trim().toLowerCase();

export async function getCustomLines(): Promise<string[]> {
  return (await getSetting<string[]>(SETTING)) ?? [];
}

/** Po uložení matky: novou linii (která není v nabídce) si zapamatovat pro příště. */
export async function rememberQueenLine(line: string | undefined) {
  const l = line?.trim();
  if (!l) return;
  const custom = await getCustomLines();
  if ([...COMMON_QUEEN_LINES, ...custom].some((x) => norm(x) === norm(l))) return;
  await setSetting(SETTING, [...custom, l]);
}

/** Výběr linie: ťuknutím z nabídky, nebo napsat / upravit vlastní. */
export function QueenLineField({ value, onChange }: { value: string | undefined; onChange: (v: string | undefined) => void }) {
  const custom = useQuery(getCustomLines, []) ?? [];
  const options = [...COMMON_QUEEN_LINES, ...custom.filter((c) => !COMMON_QUEEN_LINES.some((x) => norm(x) === norm(c)))];
  const cur = value?.trim() ?? '';
  return (
    <Field label="Linie" htmlFor="q-line" hint="Vyberte z nabídky, nebo napište vlastní – text jde upravit (např. „Singer F1“). Nová linie se zapamatuje.">
      <div className="chips" role="radiogroup" aria-label="Linie">
        {options.map((l) => {
          const on = norm(l) === norm(cur);
          return (
            <button
              key={l}
              type="button"
              role="radio"
              aria-checked={on}
              className={'chip' + (on ? ' on' : '') + (custom.includes(l) ? ' own' : '')}
              onClick={() => onChange(on ? undefined : l)}
            >
              {l}
            </button>
          );
        })}
      </div>
      <TextInput id="q-line" value={value} onChange={(v) => onChange(v || undefined)} placeholder="vlastní linie / úprava" />
    </Field>
  );
}

/** Nastavení: vlastní linie – přejmenovat, smazat, přidat. */
export function CustomLinesSetting() {
  const lines = useQuery(getCustomLines, []);
  const [editing, setEditing] = useState<number | null>(null);
  const [text, setText] = useState('');
  const [adding, setAdding] = useState('');
  if (!lines) return null;

  const save = async (next: string[]) => {
    await setSetting(SETTING, next);
    confirmSmall();
  };
  const exists = (l: string, except?: number) =>
    [...COMMON_QUEEN_LINES, ...lines.filter((_, i) => i !== except)].some((x) => norm(x) === norm(l));

  return (
    <Section title="Vlastní linie matek">
      <p className="muted small">V nabídce jsou běžné linie ({COMMON_QUEEN_LINES.join(', ')}). Sem patří vaše další linie – nabízejí se při zápisu matky.</p>
      {lines.length > 0 && (
        <ul className="list">
          {lines.map((l, i) => (
            <li key={l + i} className="list-row line-row">
              {editing === i ? (
                <>
                  <TextInput value={text} onChange={setText} autoFocus />
                  <button
                    type="button"
                    className="btn small primary"
                    onClick={async () => {
                      const t = text.trim();
                      if (!t) return toast('Vyplňte název linie.');
                      if (exists(t, i)) return toast('Tato linie už v nabídce je.');
                      await save(lines.map((x, j) => (j === i ? t : x)));
                      setEditing(null);
                    }}
                  >
                    Uložit
                  </button>
                  <button type="button" className="btn small" onClick={() => setEditing(null)}>
                    Zrušit
                  </button>
                </>
              ) : (
                <>
                  <span className="grow">{l}</span>
                  <button
                    type="button"
                    className="btn small"
                    onClick={() => {
                      setEditing(i);
                      setText(l);
                    }}
                  >
                    Upravit
                  </button>
                  <button type="button" className="btn small danger" onClick={() => save(lines.filter((_, j) => j !== i))}>
                    Smazat
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="line-row">
        <TextInput value={adding} onChange={setAdding} placeholder="nová linie" />
        <button
          type="button"
          className="btn small"
          onClick={async () => {
            const t = adding.trim();
            if (!t) return;
            if (exists(t)) return toast('Tato linie už v nabídce je.');
            await save([...lines, t]);
            setAdding('');
          }}
        >
          + Přidat
        </button>
      </div>
      <p className="small muted">Smazání linie z nabídky nemění matky, které ji už mají zapsanou.</p>
    </Section>
  );
}
